import {Injectable} from '@angular/core';
import {ChooseTopologyDataItemToVisualsMappersService} from '../choose-topology-data-item-to-visuals-mappers/choose-topology-data-item-to-visuals-mappers.service';
import {NodeApiService} from '../../../../services/api/network/online/node/node-api.service';
import {OfflineNodeApiService} from '../../../../services/api/network/offline/node/offline-node-api.service';
import {VoipPeerApiService} from '../../../../services/api/network/online/voip-peer/voip-peer-api.service';
import {OfflineVoipPeerApiService} from '../../../../services/api/network/offline/voip-peer/offline-voip-peer-api.service';
import {ConnectionApiService} from '../../../../services/api/network/online/connection/connection-api.service';
import {OfflineConnectionApiService} from '../../../../services/api/network/offline/connection/offline-connection-api.service';
import {OfflinePeerConnectionApiService} from '../../../../services/api/network/offline/peer-connection/offline-peer-connection-api.service';
import * as _ from 'lodash';
import {PeerConnectionApiService} from '../../../../services/api/peer-connection/peer-connection-api.service';
import {TopologyElementType} from '../../../../services/enums/topology-element-type.enum';


@Injectable({
  providedIn: 'root'
})
export class ChooseTopologyItemApiService {
  nodePrefix = 'node_';
  voipPeerPrefix = 'voippeer_';
  pConPrefix = 'peerConnection_';
  connectionPrefix = 'connection_';

  constructor(private dataItemToVisualsMappersService: ChooseTopologyDataItemToVisualsMappersService,
              private nodeApiService: NodeApiService,
              private offlineNodeApiService: OfflineNodeApiService,
              private voipPeerApiService: VoipPeerApiService,
              private offlineVoipPeerApiService: OfflineVoipPeerApiService,
              private connectionApiService: ConnectionApiService,
              private offlineConnectionApiService: OfflineConnectionApiService,
              private pConApiService: PeerConnectionApiService,
              private offlinePconApiService: OfflinePeerConnectionApiService) {
  }

  getDataForShapes(parameters): any {
    return Promise.all([this.nodeApiService.getNodesForMap(parameters),
      this.voipPeerApiService.getVoipPeersForMap(parameters)]).then(data => {
      const dataForShapes = {
        nodes: data[0],
        voipPeers: data[1].elements
      };
      return this.setShapes(dataForShapes);
    });
  }

  getDataForCombinedShapes(parameters): any {
    return Promise.all([this.nodeApiService.getNodesForMap(parameters), this.offlineNodeApiService.getNodesForMap(parameters),
      this.voipPeerApiService.getVoipPeersForMap(parameters),
      this.offlineVoipPeerApiService.getVoipPeersForMap(parameters)]).then(data => {
      const dataForShapes = {
        nodes: data[0],
        offlineNodes: data[1],
        voipPeers: data[2].elements,
        offlineVoipPeers: data[3]
      };
      return this.setCombinedShapes(dataForShapes);
    });
  }

  getDataForConnections(parameters): any {
    return Promise.all([this.connectionApiService.getConnectionsForMap(parameters),
      this.pConApiService.getPConsForMap(parameters)]).then(
      data => {
        const dataForConnections = {
          connections: data[0].elements,
          pCons: data[1].elements
        };
        return this.setConnections(dataForConnections);
      });
  }

  getDataForCombinedConnections(parameters): any {
    return Promise.all([this.connectionApiService.getConnectionsForMap(parameters),
      this.pConApiService.getPConsForMap(parameters),
      this.offlineConnectionApiService.getConnectionsForMap(parameters),
      this.offlinePconApiService.getPConsForMap(parameters)]).then(data => {
      const dataForConnections = {
        connections: data[0].elements,
        pCons: data[1].elements,
        offlineConnections: data[2],
        offlinePCons: data[3]
      };
      return this.setCombinedConnections(dataForConnections);
    });
  }

  private setShapes(dataForShapes): any {
    return this.mergeShapes(dataForShapes);
  }

  private setCombinedShapes(dataForShapes): any {
    const allNodes = dataForShapes.nodes.concat(dataForShapes.offlineNodes);

    const mergedNodes = allNodes.filter((node, index, self) => {
      return self.findIndex((n) => n.id === node.id) === index;
    });

    const allVoipPeers = dataForShapes.voipPeers.concat(dataForShapes.offlineVoipPeers);

    const mergedVoipPeers = allVoipPeers.filter((voipPeer, index, self) => {
      return self.findIndex((v) => v.id === voipPeer.id) === index;
    });

    return this.mergeShapes({nodes: mergedNodes, voipPeers: mergedVoipPeers});
  }

  private mergeShapes(dataForShapes): any {
    const shapes = [];
    let shape;

    for (const node of dataForShapes.nodes) {
      shape = {};
      shape.dataItem = _.cloneDeep(node);
      shape.id = this.nodePrefix + node.id;
      shape.shapeType = TopologyElementType.node;
      shape = this.dataItemToVisualsMappersService.prepareShapePropertiesForDiagram(shape);
      shapes.push(shape);
    }

    for (const vp of dataForShapes.voipPeers) {
      shape = {};
      shape.dataItem = _.cloneDeep(vp);
      shape.id = this.voipPeerPrefix + vp.id;
      shape.shapeType = TopologyElementType.voipPeer;
      shape = this.dataItemToVisualsMappersService.prepareShapePropertiesForDiagram(shape);
      shapes.push(shape);
    }
    return shapes;
  }

  private setConnections(dataForConnections): any {
    let mergedConnectionsArray;
    mergedConnectionsArray = this.mergeConnections(dataForConnections);
    return mergedConnectionsArray;
  }

  private setCombinedConnections(dataForConnections): any {

    const allConnections = dataForConnections.connections.concat(dataForConnections.offlineConnections);

    const mergedConnections = allConnections.filter((connection, index, self) => {
      return self.findIndex((c) => c.id === connection.id) === index;
    });

    const allPCons = dataForConnections.pCons.concat(dataForConnections.offlinePCons);

    const mergedPCons = allPCons.filter((pCon, index, self) => {
      return self.findIndex((p) => p.id === pCon.id) === index;
    });

    const mergedConnectionsArray = this.mergeConnections({connections: mergedConnections, pCons: mergedPCons});

    return mergedConnectionsArray;
  }

  private mergeConnections(dataForConnections): any {
    const connections = [];
    let connection;

    for (const con of dataForConnections.connections) {
      connection = {};
      connection.dataItem = _.cloneDeep(con);
      connection.id = this.connectionPrefix + con.id;
      connection.connectionType = TopologyElementType.connection;
      connection = this.dataItemToVisualsMappersService.prepareConnectionPropertiesForDiagram(connection);
      connections.push(connection);
    }

    for (const pCon of dataForConnections.pCons) {
      connection = {};
      connection.dataItem = _.cloneDeep(pCon);
      connection.id = this.pConPrefix + pCon.id;
      connection.connectionType = TopologyElementType.peerConnection;
      connection = this.dataItemToVisualsMappersService.prepareConnectionPropertiesForDiagram(connection);
      connections.push(connection);
    }

    return connections;
  }
}
